import { useState } from "react";
import { ArrowRightIcon, InboxIcon } from "lucide-react";

import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { ItemCard } from "@/components/tools/assistant/ItemCard";
import {
  formatMinutes,
  minutesBetween,
} from "@/components/tools/assistant/format";
import { Chip, Tag } from "@/components/tools/assistant/shared";
import {
  type ChipDef,
  ITEM_STATUS,
  itemStatus,
  MESSAGE_KIND,
  ORIGIN,
} from "@/components/tools/assistant/vocab";

import { useGetAssistantItems } from "@/hooks/queries/useGetAssistantAdmin";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { AssistantItemDelivery, AssistantItemRow } from "@/types";

type Filter = "all" | "open" | "reply" | "evidence" | "staffer";

const FILTERS: { key: Filter; def: ChipDef | null }[] = [
  { key: "all", def: null },
  { key: "open", def: ITEM_STATUS.open },
  { key: "reply", def: ITEM_STATUS.reply },
  { key: "evidence", def: ITEM_STATUS.evidence },
  { key: "staffer", def: ITEM_STATUS.staffer },
];

const matches = (item: AssistantItemRow, filter: Filter) =>
  filter === "all" || itemStatus(item) === ITEM_STATUS[filter];

export const AssistantItemsTab = ({
  days,
  onOpenThread,
}: {
  days: number;
  onOpenThread: (threadId: string) => void;
}) => {
  const [filter, setFilter] = useState<Filter>("all");
  const { data: items, status } = useGetAssistantItems({ days });

  if (status === "pending") return <LoadingIndicator variant="medium" />;
  if (status === "error") return <ErrorText errorMessage="Error loading items." />;

  const shown = items.filter((i) => matches(i, filter));

  return (
    <Column className="min-h-0 flex-1 gap-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(({ key, def }) => {
          const count = items.filter((i) => matches(i, key)).length;
          return (
            <button
              className={cn(
                "rounded-full ring-offset-2 transition",
                filter === key ? "ring-2 ring-slate-400" : "opacity-80 hover:opacity-100",
              )}
              key={key}
              onClick={() => {
                setFilter(key);
              }}
              type="button"
            >
              {def ? (
                <Chip count={count} def={def} />
              ) : (
                <Tag>
                  <span className="font-semibold">{count}</span>&nbsp;All emails
                </Tag>
              )}
            </button>
          );
        })}
      </div>
      {shown.length === 0 ? (
        <EmptyState
          description="Customer emails the assistant surfaces show up here."
          Icon={InboxIcon}
          title="Nothing here in this window"
        />
      ) : (
        <Column className="gap-3 overflow-auto pb-6">
          {shown.map((item) => (
            <ItemCard
              footer={<DeliveryTimeline deliveries={item.deliveries} />}
              header={
                <button
                  className="mr-1 rounded-full bg-slate-900 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-700"
                  onClick={() => {
                    onOpenThread(item.threadId);
                  }}
                  title="Open this Staffer's conversation"
                  type="button"
                >
                  {item.staffer?.name ?? "Unknown"}
                </button>
              }
              item={item}
              key={item.key}
            />
          ))}
        </Column>
      )}
    </Column>
  );
};

/** Every message that carried this email, in order, with why it went out then. */
const DeliveryTimeline = ({
  deliveries,
}: {
  deliveries: AssistantItemDelivery[];
}) => (
  <div className="flex flex-wrap items-center gap-1.5 border-t pt-2">
    <span className="text-xs font-medium text-slate-500">Sent to Staffer:</span>
    {deliveries.map((d, i) => (
      <span className="inline-flex items-center gap-1.5" key={`${d.at}-${i.toString()}`}>
        {i > 0 && <ArrowRightIcon className="size-3 text-slate-300" />}
        <Chip def={MESSAGE_KIND[d.kind]} detail={dates.formatShortDateTime(d.at)} />
        {d.origin === "held_for_chat" && (
          <Chip
            def={ORIGIN.held_for_chat}
            detail={
              d.plannedAt
                ? `waited ${formatMinutes(minutesBetween(d.plannedAt, d.at))}`
                : undefined
            }
          />
        )}
        {d.origin === "follow_up" && (
          <Chip def={ORIGIN.follow_up} detail={d.why ?? undefined} />
        )}
      </span>
    ))}
  </div>
);
